import { useRef, useState, useCallback } from 'react';

import type { Tab } from '@/types/tabTypes';

interface DragState {
  tabId: string;
  originalIndex: number;
  currentIndex: number;
  deltaX: number;
  tabRects: { left: number; width: number }[];
}

interface UseTabDragArgs {
  tabs: Tab[];
  onTabClick?: (id: string) => void;
  onReorderTabs?: (fromId: string, toId: string) => void;
}

export function useTabDrag({
  tabs,
  onTabClick,
  onReorderTabs,
}: UseTabDragArgs) {
  const tabElemsRef = useRef<Map<string, HTMLDivElement>>(new Map());
  const dragStateRef = useRef<DragState | null>(null);
  const [dragState, setDragState] = useState<DragState | null>(null);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, tab: Tab, index: number) => {
      if (e.button !== 0) return;
      e.preventDefault();

      const startMouseX = e.clientX;

      const tabRects = tabs.map((t) => {
        const el = tabElemsRef.current.get(t.id);
        if (!el) return { left: 0, width: 100 };
        const r = el.getBoundingClientRect();
        return { left: r.left, width: r.width };
      });

      const initialState: DragState = {
        tabId: tab.id,
        originalIndex: index,
        currentIndex: index,
        deltaX: 0,
        tabRects,
      };
      dragStateRef.current = initialState;
      setDragState(initialState);

      const onMouseMove = (e: MouseEvent) => {
        if (!dragStateRef.current) return;
        const { originalIndex, tabRects } = dragStateRef.current;
        const deltaX = e.clientX - startMouseX;

        const draggedCenter =
          tabRects[originalIndex].left +
          tabRects[originalIndex].width / 2 +
          deltaX;

        let currentIndex = 0;
        let minDist = Infinity;
        tabRects.forEach(({ left, width }, i) => {
          const dist = Math.abs(draggedCenter - (left + width / 2));
          if (dist < minDist) {
            minDist = dist;
            currentIndex = i;
          }
        });

        const newState: DragState = {
          ...dragStateRef.current,
          deltaX,
          currentIndex,
        };
        dragStateRef.current = newState;
        setDragState({ ...newState });
      };

      const onMouseUp = (e: MouseEvent) => {
        const ds = dragStateRef.current;
        const totalDelta = Math.abs(e.clientX - startMouseX);

        if (totalDelta < 4) {
          onTabClick?.(tab.id);
        } else if (ds && ds.originalIndex !== ds.currentIndex) {
          onReorderTabs?.(ds.tabId, tabs[ds.currentIndex].id);
        }

        dragStateRef.current = null;
        setDragState(null);
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('mouseup', onMouseUp);
      };

      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    },
    [tabs, onTabClick, onReorderTabs],
  );

  const getTabStyle = (tab: Tab, index: number): React.CSSProperties => {
    if (!dragState) return {};

    const { tabId, originalIndex, currentIndex, deltaX, tabRects } = dragState;
    const draggedWidth = tabRects[originalIndex]?.width ?? 100;
    const isDragging = Math.abs(deltaX) > 3;

    if (tab.id === tabId) {
      return {
        transform: `translateX(${deltaX}px)`,
        transition: 'none',
        boxShadow: isDragging ? '0 4px 16px rgba(0,0,0,0.5)' : undefined,
      };
    }

    let shift = 0;
    if (currentIndex < originalIndex) {
      if (index >= currentIndex && index < originalIndex) shift = draggedWidth;
    } else if (currentIndex > originalIndex) {
      if (index > originalIndex && index <= currentIndex) shift = -draggedWidth;
    }

    return {
      transform: `translateX(${shift}px)`,
      transition: 'transform 120ms ease',
    };
  };

  return { tabElemsRef, dragState, handleMouseDown, getTabStyle };
}
